import React, { useState } from 'react';
import { Log, User as OidcUser, UserManager } from 'oidc-client-ts';
import { useLocation, useNavigate } from 'react-router-dom';

import { signoutUrl, userManagerSettings } from '../../utils/config';
import { updateAxiosAuthorization } from '../../utils/axios';
import { UserProfile } from '../../interfaces';
import { useErrorInterceptor } from '../../hooks/use-error-interceptor';
import { AuthContext } from './auth.context';
import { AuthCookiesManager } from '../../utils/cookies-manager';
import { TokenRenewalService } from '../../services';

const transformToUserProfile = (user: OidcUser | null): UserProfile | null => {
  if (!user || !user.profile) {
    return null;
  }
  return {
    name: `${user.profile.firstname} ${user.profile.lastname}`,
    email: user.profile.email ?? '',
    role: user.profile.role,
    id: user.profile.user,
    firstName: user.profile.firstname,
    lastName: user.profile.lastname,
    mobilePhone: user.profile.mobilephone
  } as UserProfile;
};

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  useErrorInterceptor();
  const userManager = React.useMemo(() => new UserManager(userManagerSettings), []);
  const tokenRenewalService = React.useMemo(
    () => new TokenRenewalService(userManager),
    [userManager]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<UserProfile | null>(null);

  const setUserProfile = (user: OidcUser | null) => {
    const userProfile = transformToUserProfile(user);
    updateAxiosAuthorization(user?.access_token);
    if (userProfile) {
      AuthCookiesManager.setUserId(userProfile.id);
    }
    setUser(userProfile);
  };

  const getUser = async (): Promise<void> => {
    const user = await userManager.getUser();
    setUserProfile(user);
  };

  React.useLayoutEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      Log.setLogger(console);
      Log.setLevel(Log.DEBUG);
    }

    getUser();

    // Start proactive token renewal
    tokenRenewalService.startProactiveRenewal();

    userManager.events.addAccessTokenExpired(() => {
      signout();
    });

    return () => {
      tokenRenewalService.stopProactiveRenewal();
    };
  }, []);

  const signinRedirect = (): Promise<void> => {
    return userManager.signinRedirect({ url_state: location.pathname });
  };

  const signinRedirectCallback = async (): Promise<void> => {
    const authedUser = await userManager.signinRedirectCallback();
    setUserProfile(authedUser);
    const pathname = authedUser.url_state ?? '/';
    navigate(pathname, { replace: true });
  };

  const signinSilent = async (): Promise<void> => {
    const authedUser = await userManager.signinSilent();
    setUserProfile(authedUser);
  };

  const logout = async (): Promise<void> => {
    localStorage.clear();
    sessionStorage.clear();
    AuthCookiesManager.deleteCookie();
    await userManager.clearStaleState();
    await userManager.removeUser();
    // Stop proactive token renewal
    tokenRenewalService.stopProactiveRenewal();
  };

  const signout = async (): Promise<void> => {
    await logout();
    window.location.replace(signoutUrl);
  };

  const signoutRedirectCallback = async (): Promise<void> => {
    await userManager
      .signoutCallback()
      .then(logout)
      .finally(() => {
        navigate('/', { replace: true });
      });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signinRedirect,
        signinRedirectCallback,
        signoutRedirectCallback,
        signinSilent,
        signout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
